import type { LoaderFunctionArgs } from "react-router-dom";
import type { CascaderProps } from "antd";
import type { TableColumnsType } from "antd";
import dayjs from "dayjs";
import Big from "big.js";
import EventTargetLocal from "./shared/EventTarget";

export const classNames = (...classList: Array<any>): string => {
  const result = [];
  for (let i = 0; i < classList.length; i++) {
    if (typeof classList[i] === "string") {
      result.push(classList[i]);
    } else if (typeof classList[i] === "object" && classList[i] !== null) {
      Object.keys(classList[i]).forEach((key: string) => {
        if (classList[i][key]) {
          result.push(key);
        }
      });
    }
  }
  return result.join(" ").trim();
};

export const PHONE_RX =
  /^1(3\d|4[5-9]|5[0-35-9]|6[2567]|7[0-8]|8\d|9[0-35-9])\d{8}$/;

// 倒计时标准实现
// https://www.youtube.com/watch?v=MCi6AZMkxcU
let getCurrentTime: () => number | DOMHighResTimeStamp;
const hasPerformanceNow =
  typeof performance === "object" && typeof performance.now === "function";

if (hasPerformanceNow) {
  const localPerformance = performance;
  getCurrentTime = () => localPerformance.now();
} else {
  const localDate = Date;
  const initialTime = localDate.now();
  getCurrentTime = () => localDate.now() - initialTime;
}

export const countdown = (callback: (seconds: number) => void) => {
  const start = getCurrentTime();
  let timer = 0;
  let callbackId = 0;
  let isClose = false;
  function frame(time: number) {
    if (isClose) {
      return;
    }
    const elapsed = time - start;
    const seconds = Math.round(elapsed / 1000);
    callback(seconds);
    const targetNext = (seconds + 1) * 1000 + start;
    timer = window.setTimeout(() => {
      callbackId = requestAnimationFrame(frame);
    }, targetNext - getCurrentTime());
  }
  frame(start);
  return function close() {
    isClose = true;
    if (timer) {
      timer = 0;
      window.clearTimeout(timer);
    }
    if (callbackId) {
      callbackId = 0;
      cancelAnimationFrame(callbackId);
    }
  };
};

export const noop = () => {};

export const getV = <T>(obj: T): any => {
  return (...rest: string[]) => {
    var arr = rest;
    return arr.reduce(function (result: T, props: string) {
      if (typeof result === "object" && result !== null) {
        return (result as any)[props];
      }
      return undefined;
    }, obj);
  };
};

export const hidePhone = (value: any) => {
  if (typeof value === "number" && !isNaN(value)) {
    const v = String(value);
    return v.slice(0, 3) + "****" + v.slice(7);
  }
  if (typeof value === "string") {
    return value.slice(0, 3) + "****" + value.slice(7);
  }
  return "";
};

// 判断是否react-router
// https://github.com/remix-run/react-router/blob/main/packages/router/router.ts
export const isResponse = (value: any): value is Response => {
  return (
    value != null &&
    typeof value.status === "number" &&
    typeof value.statusText === "string" &&
    typeof value.headers === "object" &&
    typeof value.body !== "undefined"
  );
};

// 合并react-router loader
export const mergeLoader = (
  functions: Array<Record<string, any> | ((loader: LoaderFunctionArgs) => any)>
) => {
  return (loader: LoaderFunctionArgs) => {
    let result: any = undefined;
    for (let i = 0; i < functions.length; i++) {
      let data: any = functions[i];
      if (typeof functions[i] === "function") {
        data = (functions[i] as Function)(loader);
      }
      if (isResponse(data)) {
        return data;
      }
      if (typeof data === "object" && data !== null) {
        if (result === undefined) {
          result = {};
        }
        Object.assign(result, data);
      }
    }
    return result;
  };
};

export const toArray = (v: any): Array<any> => {
  if (Array.isArray(v)) {
    return v;
  }
  return [];
};

export const list2Map = (
  list: any[],
  k: string,
  v: string
): {
  [k in string]: string;
} => {
  return list.reduce(
    (o, s) => {
      o[s[k]] = s[v];
      return o;
    },
    {} as {
      [k in string]: string;
    }
  );
};

export const map2Options = (
  map: Record<string, string>
): {
  label: string;
  value: string;
}[] => {
  return Object.keys(map).map((key) => {
    return {
      label: map[key],
      value: key,
    };
  });
};

export const labelPick = (
  map: { [k in string]: string },
  k: string
): string | undefined => {
  if (map && k) {
    const keys = Object.keys(map);
    const diffKey = k.toLowerCase();
    for (let i = 0; i < keys.length; i++) {
      if (keys[i].toLowerCase() === diffKey) {
        return map[keys[i]];
      }
    }
    return k;
  }
  return undefined;
};

export const list2Options = (
  list: any[],
  k: string,
  v: string
): {
  label: string;
  value: string;
}[] => {
  return list.map((item) => {
    return {
      label: item[k],
      value: item[v],
    };
  });
};

export const pickObject = (
  obj: Record<string, any> | any[],
  filter: (k: string | number, v: any) => boolean
) => {
  if (Array.isArray(obj)) {
    return obj.filter((v, i) => filter(i, v));
  }
  return Object.keys(obj).filter((k) => filter(k, obj[k]));
};

export const renderDate = (v: unknown): string => {
  if (typeof v === "string" || (typeof v === "number" && !isNaN(v))) {
    const date = new Date(v);
    if (isNaN(date.getTime())) {
      return "";
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate() + 1).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return "";
};

export const formatIOS8601 = (
  times: any,
  options?: {
    join?: string;
    format?: string;
  }
) => {
  const o = {
    join: "~",
    format: "YYYY-MM-DD HH:mm:ss",
  };
  Object.assign(o, options);
  if (Array.isArray(times)) {
    if (times.length > 1) {
      if (typeof times[0] === "string") {
        const start = dayjs(times[0]).format(o.format);
        const end = dayjs(times[1]).format(o.format);
        return start + o.join + end;
      }
    }
    return "";
  }
  if (typeof times === "string") {
    return dayjs(times).format(o.format);
  }
  return "";
};

export const renderMemory = (
  v: number,
  unit?: "B" | "KB" | "MB" | "GB"
): string => {
  if (typeof v === "number" && !isNaN(v)) {
    let coefficient = 1073741824;
    switch (unit) {
      case "KB":
        coefficient = 1024 * 1024;
        break;
      case "MB":
        coefficient = 1024;
        break;
      case "GB":
        coefficient = 1;
        break;
    }
    v = v / coefficient;
    if (v >= 1024) {
      return Math.floor(v / 1024) + " T";
    }
    return Math.floor(v * 10) / 10 + " GB";
  }
  return "";
};

export const fileSizeToString = (size: number): string => {
  if (size) {
    size /= 1024;
    if (size < 1024) {
      return size.toFixed(2) + "KB";
    }
    size /= 1024;
    if (size < 1024) {
      return size.toFixed(2) + "MB";
    }
    size /= 1024;
    if (size < 1024) {
      return size.toFixed(2) + "GB";
    }
    size /= 1024;
    return size.toFixed(2) + "TB";
  }
  return "0";
};

// const _filter_name = ['@', 'Core', 'GHz', 'Intel', 'CPU', 'Processor']
export const renderCpu = (v: string): string => {
  return v;
  // if (typeof v === 'string') {
  //   return v.split(' ').map(p => {
  //     for (let i = 0, l = _filter_name.length; i < l ;i++) {
  //       if (p.includes(_filter_name[i])) {
  //         return ''
  //       }
  //     }
  //     return p
  //   }).join(' ')
  // }
  // return ''
};

export const toNum = (v: unknown): number => {
  if (v == null) {
    return 0;
  }
  if (typeof v === "string") {
    v = parseFloat(v);
  }
  if (typeof v === "number" && !isNaN(v)) {
    return v;
  }
  return 0;
};

export const renderRate = (v: number, present = "%"): string => {
  if (typeof v === "number" && !isNaN(v)) {
    return v.toFixed(2) + present;
  }
  return "";
};

export const isUnder = (v: unknown): v is undefined & null => {
  return typeof v === "undefined" || v === null;
};

export const renderValue = (v: unknown): string => {
  if (typeof v === "string") {
    return v;
  }
  if (typeof v === "number") {
    return isNaN(v) ? "" : String(v);
  }
  if (isUnder(v)) {
    return "";
  }
  if (typeof v === "object") {
    return JSON.stringify(v);
  }
  return "";
};

export const getSkuDesc = (type?: string): string => {
  if (typeof type === "string") {
    switch (type.toLowerCase()) {
      case "payg":
        return "时";
      case "period":
        return "时";
      case "day":
        return "日";
      case "week":
        return "周";
      case "month":
        return "月";
    }
  }
  return "";
};

const deepClone = (data: any) => {
  const clone = (v: any) => {
    let result = v;
    if (typeof v === "object" && v !== null) {
      if (Array.isArray(v)) {
        result = [];
        v.forEach((d: any) => {
          result.push(clone(d));
        });
      } else {
        result = {};
        Object.keys(v).forEach((k) => {
          result[k] = clone(v[k]);
        });
      }
    }
    return result;
  };
  return clone(data);
};

export const isNumStr = (str?: string | number | null): boolean => {
  if (str == null) {
    return false;
  }
  if (typeof str === "string") {
    str = parseFloat(str);
  }
  if (typeof str === "number" && !isNaN(str)) {
    return true;
  }
  return false;
};

export const cascaderAttachTitle = (
  option: CascaderProps["options"],
  titles: string[]
) => {
  let options = deepClone(option);
  const attach = (children: CascaderProps["options"], index: number) => {
    if (!children) {
      return;
    }
    // debugger
    // 判断子类是否包含titles[index] || "选择"，如果包含就不需要继续添加了
    if (children.some((c) => c.label === (titles[index] || "选择"))) {
      return;
    }
    children.unshift({
      label: titles[index] || "选择",
      value: `${index + 10086}`,
      disabled: true,
    });
    for (let i = 1, l = children.length; i < l; i++) {
      attach(children[i].children, index + 1);
    }
  };
  attach(options, 0);
  return options;
};

export const hasToken = () => {
  let token = null;
  const tokenStr = localStorage.getItem("token");
  if (tokenStr) {
    token = JSON.parse(tokenStr);
    if (token && token.access_token) {
      return true;
    }
  }
  return false;
};

export const computeTableX = (columns: TableColumnsType) => {
  return columns.reduce((t: any, v: any) => {
    t += v.width || 0;
    return t;
  }, 0);
};

export const computeSpeed = (flow: number | string) => {
  if (typeof flow === "string") {
    flow = parseFloat(flow);
  }
  if (typeof flow === "number" && !isNaN(flow)) {
    flow = Math.floor((flow * 8) / 1048576);
  }
  return flow;
};

export const download = (link: string, fileName: string) => {
  if (!link) {
    return;
  }
  if (!fileName) {
    fileName = "文件";
  }
  const a = document.createElement("a");
  document.body.appendChild(a);
  a.style.display = "none";
  a.href = link;
  a.download = fileName;
  a.click();
  a.remove();
};

export const renderTimeUnit = (time: any): string => {
  if (typeof time === "number") {
    if (isNaN(time)) {
      return "";
    }
    const h = Math.floor(time / 3600);
    time = time - h * 3600;
    const m = Math.floor(time / 60);
    const s = time - 60 * m;
    let result = "";
    if (h) {
      result += h + "时";
    }
    if (m) {
      result += m + "分";
    }
    if (s) {
      result += s + "秒";
    }
    return result;
  }
  return "";
};

export const clearKeyForEmptyValue = (
  obj: Record<string, any>
): Record<string, any> => {
  const set: Record<string, any> = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] != null) {
      set[key] = obj[key];
    }
  });
  return set;
};

export const withResolves = <T = unknown>() => {
  let resolve: (value: T) => void;
  let reject: (reason?: any) => void;
  const promise = new Promise<T>((resolve$$, reject$$) => {
    resolve = resolve$$;
    reject = reject$$;
  });
  return {
    promise,
    resolve: resolve!,
    reject: reject!,
  };
};

export const createListKey = (
  list: any,
  pagination?: {
    size: number;
    page: number;
  }
) => {
  if (list && list.length) {
    if (pagination && pagination.page && pagination.size) {
      const start = (pagination.page - 1) * pagination.size;
      return list.map((item: any, index: number) => {
        return {
          ...item,
          $$key: start + index,
        };
      });
    }
  }
  return list;
};

export const renderEmpty = (value: unknown): string => {
  if (typeof value === "number" && !isNaN(value)) {
    return String(value);
  }
  if (typeof value === "string") {
    return value.trim() || "-";
  }
  return "-";
};

export const objectIsAllEmpty = (
  value?: Record<string, any> | null
): boolean => {
  if (typeof value === "object" && value !== null) {
    return Object.keys(value).some((k) => {
      const v = value[k];
      if (v === undefined || v === null) {
        return false;
      }
      if (typeof v === "string") {
        return v.trim() ? true : false;
      }
      if (typeof v === "number" && isNaN(v)) {
        return false;
      }
      return true;
    })
      ? false
      : true;
  }
  return true;
};

export const trimMiddle = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) {
    return String(str);
  }
  let leftHalf = maxLength >> 1;
  let rightHalf = maxLength - leftHalf - 1;
  if ((str.codePointAt(str.length - rightHalf - 1) as number) >= 0x10000) {
    --rightHalf;
    ++leftHalf;
  }
  if (leftHalf > 0 && (str.codePointAt(leftHalf - 1) as number) >= 0x10000) {
    --leftHalf;
  }
  return (
    str.substr(0, leftHalf) +
    "…" +
    str.substr(str.length - rightHalf, rightHalf)
  );
};

// 判断是否当前环境
export const isEnv = (env: "test" | "prd"): boolean => {
  const hostname = window.location.hostname;
  if (env === "test") {
    if (hostname === "localhost") {
      return true;
    }
    return hostname.split(".").includes(env);
  }
  return true;
};

export const formatTime = (
  dateTime: string | undefined | null,
  format: string
) => {
  if (dateTime) {
    const s = dateTime.split(":");
    return `${s[0] || ""}:${s[1] || ""}`;
  }
  return "";
};

export const toolsUrl = (
  host: string | undefined,
  port: number | undefined,
  pathname: string | null,
  params: Record<string, string | number | undefined>
): null | string => {
  if (!host) {
    return null;
  }
  let newUrl = host;
  if (port) {
    newUrl += ":" + port;
  }
  if (pathname) {
    newUrl += pathname;
  }
  const search = Object.keys(params)
    .reduce((s, k) => {
      if (params[k] != null) {
        s += `&${k}=${params[k]}`;
      }
      return s;
    }, "")
    .slice(1);
  if (search) {
    newUrl += "?" + search;
  }
  if (/^http?:(s)?:/.test(newUrl) === false) {
    newUrl = "http://" + newUrl;
  }
  return newUrl;
};

export const computeSwellCost = (swell: number): number => {
  if (swell > 75) {
    const base = new Big(0.0004)
    const multiple = (swell - 50) * 24
    return base.times(multiple).toNumber()
  }
  if (swell > 50) {
    return 0.24
  }
  return 0
}

export const fillList = (list: any[], count: number): any[] => {
  const bucket: any = [];
  let start = 0;
  let index = 0;
  while (start < count) {
    if (bucket.length < index + 1) {
      bucket.push([]);
    }
    const curr = bucket[index];
    curr.push(list[index]);
    start += 1;
    index += 1;
    if (index === list.length) {
      index = 0;
    }
  }
  const result: any = [];
  bucket.forEach((gourd: any[]) => {
    gourd.forEach((water: any) => {
      result.push(water);
    });
  });
  return result;
};


/**
 * 将原始数据转换为树形结构
 * @param {Array} data 原始数据数组
 * @returns {Array} 树形结构数组
 */
interface ItemData {
  name: string;
  os_family: string;
  os_version: string;
  id: string;
  // Add other properties as needed
}

interface TreeNode {
  value: string;
  label: string;
  children?: TreeNode[];
}

/**
 * 将原始数据转换为树形结构
 * @param {Array} data 原始数据数组
 * @returns {Array} 树形结构数组
 */
export const transformToTreeData = (data: any[]): TreeNode[] => {
  // 按 name 分组
  const groupedByName: Record<string, ItemData[]> = data.reduce((acc, item) => {
    if (!acc[item.name]) {
      acc[item.name] = [];
    }
    acc[item.name].push(item);
    return acc;
  }, {} as Record<string, ItemData[]>);

  // 转换为树形结构
  return Object.entries(groupedByName).map(([name, items]) => ({
    value: name,
    label: name.charAt(0).toUpperCase() + name.slice(1), // 首字母大写
    children: items.map((item) => ({
      value: `${item.id}`,
      label: `${item.os_version}`
    }))
  }));
  // return Object.entries(groupedByName).map(([name, items]) => ({
  //   value: name,
  //   label: name.charAt(0).toUpperCase() + name.slice(1), // 首字母大写
  //   children: items.map((item) => ({
  //     value: `${item.os_family}-${item.id}`,
  //     label: `${item.os_family} ${item.os_version}`,
  //     children: [
  //       {
  //         value: item.os_version,
  //         label: item.os_version
  //       }
  //     ]
  //   }))
  // }));
}

export const globalEvent = new EventTargetLocal();
